import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Avatar, Badge, Box, Center, Flex, Heading, HStack, Icon, Image, List, Separator, Spinner, Stack, Text, VStack, Wrap } from '@chakra-ui/react';
import { ArticleGrid } from '@src/components/sco/ArticleGrid';
import { ButtonLink } from '@src/components/sco/ButtonLink.tsx';
import { EducationErrorState } from '@src/components/sco/Education/EducationErrorState';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { BreakpointContextProvider } from '@src/components/sco/utilities/BreakpointProvider';
import { SEOMetadata } from '@src/components/sco/utilities/SEOMetadata';
import { gql } from '@src/lib/gql';
import React from 'react';
import { FaXTwitter } from 'react-icons/fa6';
import { LuGlobe, LuLinkedin } from 'react-icons/lu';
type AuthorProfileProps = {
  slug: string;
};
const twitterHref = (value: string) => value.startsWith('http') ? value : `https://x.com/${value.replace(/^@/, '')}`;
export const AuthorProfile = props => {
  const $ = _c(23);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = AuthorProfile.queries.person();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.slug) {
    t1 = {
      variables: {
        slug: props.slug
      }
    };
    $[1] = props.slug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useQuery(t0, t1);
  const person = query.data?.person;
  let t2;
  if ($[3] !== person) {
    t2 = person ? [person.firstName, person.lastName].filter(Boolean).join(" ") : "";
    $[3] = person;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const name = t2;
  let t3;
  if ($[5] !== person) {
    t3 = person ? [person.website && {
      icon: LuGlobe,
      label: "Website",
      href: person.website
    }, person.twitter && {
      icon: FaXTwitter,
      label: "X / Twitter",
      href: twitterHref(person.twitter)
    }, person.linkedInUrl && {
      icon: LuLinkedin,
      label: "LinkedIn",
      href: person.linkedInUrl
    }].filter(Boolean as unknown as (v: any) => v is {
      icon: any;
      label: string;
      href: string;
    }) : [];
    $[5] = person;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const socialLinks = t3;
  let t4;
  if ($[7] !== query.loading) {
    t4 = query.loading && <Center padding={10}><Spinner /></Center>;
    $[7] = query.loading;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== query.error) {
    t5 = query.error && <Center padding={10}><Text color="red.500">{`Error: ${query.error.message}`}</Text></Center>;
    $[9] = query.error;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== person || $[12] !== query.loading) {
    t6 = !query.loading && !person && <EducationErrorState ctaHref="/authors" ctaLabel="Explore All Authors" headingText="Author not found." />;
    $[11] = person;
    $[12] = query.loading;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== name || $[15] !== person || $[16] !== socialLinks) {
    t7 = person && <Box maxWidth="container.md" paddingX={6} paddingY={[8, 10, 12]} width="full"><SEOMetadata description={person.byline} imageUrl={person.avatar?.url} title={name} /><VStack gap={4} textAlign="center"><Avatar.Root height="8rem" size="2xl" width="8rem"><Avatar.Fallback name={name} />{person.avatar?.url && <Avatar.Image alt={name} src={person.avatar.url} />}</Avatar.Root><VStack gap={1}><Heading size={["2xl", "3xl"]}>{name}</Heading>{person.title && <Text color="fg.muted" textStyle="lg">{person.title}</Text>}{person.companyName && <HStack color="fg.muted" gap={2}>{person.companyLogo?.url && <Image alt={person.companyName} height="1.25rem" src={person.companyLogo.url} />}<Text textStyle="md">{person.companyName}</Text></HStack>}{person.byline && <Text color="fg.subtle" marginTop={1} textStyle="md">{person.byline}</Text>}</VStack>{socialLinks.length > 0 && <HStack gap={4} marginTop={2}>{socialLinks.map(_temp)}</HStack>}</VStack><Stack gap={8} marginTop={10}>{person.knownFor && <Section title="Known For"><Text whiteSpace="pre-wrap">{person.knownFor}</Text></Section>}{person.shortBio && <Section title="About"><MarkdownRenderer preset="article">{person.shortBio}</MarkdownRenderer></Section>}{person.fullBio && <Section title={person.shortBio ? "Full Bio" : "About"}><MarkdownRenderer preset="article">{person.fullBio}</MarkdownRenderer></Section>}{person.notables?.length > 0 && <Section title="Notables"><List.Root>{person.notables.map(_temp2)}</List.Root></Section>}{person.topTips?.length > 0 && <Section title="Top Tips"><List.Root>{person.topTips.map(_temp3)}</List.Root></Section>}{person.quotes?.length > 0 && <Section title="Quotes"><Stack gap={4}>{person.quotes.map(_temp4)}</Stack></Section>}{person.expertise?.length > 0 && <Section title="Expertise"><Wrap gap={1} justify="flex-start">{person.expertise.map(_temp5)}</Wrap></Section>}<Section title="Articles"><BreakpointContextProvider><ArticleGrid filters={{
              authorSlugs: [person.slug]
            }} /></BreakpointContextProvider></Section></Stack></Box>;
    $[14] = name;
    $[15] = person;
    $[16] = socialLinks;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== t7) {
    t8 = <Flex align="center" direction="column" justify="stretch" width="full">{t4}{t5}{t6}{t7}</Flex>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
const Section = props => {
  const $ = _c(6);
  let t0;
  if ($[0] !== props.title) {
    t0 = <Heading marginBottom={3} size="md">{props.title}</Heading>;
    $[0] = props.title;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Separator marginBottom={4} />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.children || $[4] !== t0) {
    t2 = <Box width="full">{t0}{t1}{props.children}</Box>;
    $[3] = props.children;
    $[4] = t0;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
AuthorProfile.queries = {
  person: () => gql`
    query ($slug: String!) {
      person(where: { slug: $slug }) {
        id
        slug
        firstName
        lastName
        title
        byline
        companyName
        companyLogo {
          id
          url
          altText
        }
        avatar {
          id
          url(transformation: { image: { resize: { fit: crop, width: 600, height: 600 } } })
          altText
        }
        shortBio
        fullBio
        knownFor
        notables
        quotes
        topTips
        twitter
        website
        email
        expertise {
          id
          name
          slug
        }
        linkedInUrl
      }
    }
  `
};
function _temp(link) {
  return <ButtonLink key={link.label} href={link.href} target="_blank" title={link.label} variant="outline"><Icon as={link.icon} boxSize={5} /> {link.label}</ButtonLink>;
}
function _temp2(item, i) {
  return <List.Item key={i}>{item}</List.Item>;
}
function _temp3(tip, i_0) {
  return <List.Item key={i_0}>{tip}</List.Item>;
}
function _temp4(quote, i_1) {
  return <Box key={i_1} borderColor="colorPalette.solid" borderLeft="3px solid" color="fg.muted" fontStyle="italic" paddingLeft={4}>{quote}</Box>;
}
function _temp5(term) {
  return <Badge key={term.id}>{term.name}</Badge>;
}